import { Auth0PalmettoFinanceUser, FlatOrganization, OrganizationNode } from '@/types/Organizations';

import {
  Actor,
  WebhookClient,
  WebhookSubscription,
  DeepPartial,
  EmailSetting,
  SignatoryName,
  FinancialAccountDocument,
  FinancialAccountRegister,
  HoldbackPaymentBatchedSettings,
  HoldbackPayoutEventsSettings,
  PausedSettings,
  OrgAccountsImpacted,
  OrganizationClawbackSettingsV2,
  OrganizationCoverageAreaSettingsDocument,
  CoverageAreaValue,
  OrganizationDirectPaySettingsV2,
  OrganizationEquipmentSettingsV2,
  OrganizationHoldbackPaymentBatchedSettingsDocument,
  OrganizationHoldbackPayoutEventsSettingsDocument,
  OrganizationPausedSettingsDocument,
  OrganizationPausedSettingsType,
  OrganizationInvoiceSettingsV2,
  OrganizationMaterialClawbackSettingsV2,
  OrganizationMilestoneClawbackSettingsV2,
  OrganizationSettingsTypes,
  OrganizationSettingsV2,
  OrganizationSettingsV2Types,
  OrganizationStatus,
  OrganizationType,
  OrganizationUnderwritingSettingsV2,
  OrgHardwareExceptionsResponse,
  OrgV2PaymentPlanSettings,
  OrgV2ReviewSlaSettings,
  OrgV2ReviewSlaSettingsResponse,
  PaymentPlan,
  PaymentPlanHistory,
  PayoutHold,
  PendingOrganizationPaymentPlanChanges,
  PriceSheetMapping,
  ProgramType,
  ListOrgHardwareExceptionsParams,
  StateLicenseNumber,
  WithObjectIdsAsStrings,
  ClientWebhookResponse,
  TestWebhookSubscription,
  OutboundWebhookLog,
  WebhookSubscriptionPostResponse,
  WarrantyContractSkuTermMapping,
  MaintenancePeriodUnit,
  MaintenanceRateUnit,
  OrganizationMaintenanceSettingsV2Value,
} from 'types';

import { api } from './api';

interface CreateOrganizationParams {
  alias: string;
  parentAlias: string;
  name: string;
  email?: string;
  phoneNumber?: string;
  federalEmployerId?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  licenses?: StateLicenseNumber[];
  insuranceCompanyName?: string;
  insuranceCompanyPhoneNumber?: string;
  programTypes?: ProgramType[];
  organizationTypes?: OrganizationType[];
  extendedWarrantyDealerInformation?: Array<{
    warrantyProvider: string;
    dealerLicenseNumber: string;
    isPreferredProvider?: boolean;
    contractSkuTermMappings?: WarrantyContractSkuTermMapping[];
    contractSku?: string;
  }>;
  status: OrganizationStatus;
  statusChangeDescription?: string;
  accountBundlingEnabled?: boolean;
}

interface GetOrganizationQueryParams {
  includeUsers?: boolean;
  useRootOrganization?: boolean;
  usersPage?: number;
  usersLimit?: number;
  search?: string;
  programType?: string;
  state?: string;
  advancedFilters?: string;
}

interface GetOrganizationDetailsQueryParams {
  alias: string;
  includeUsers?: boolean;
  usersPage?: number;
  usersLimit?: number;
  inviteToken?: string;
}

interface GetOrganizationWebhookStatisticsResponse {
  _id: string;
  successCount: number;
  failureCount: number;
  skipCount: number;
}

interface GetOrganizationPaymentSchedule {
  alias: string;
}

interface GetOrganizationEmailSettings {
  alias: string;
}

interface GetOrganizationInvoiceSettings {
  alias: string;
  programType?: ProgramType;
}

interface PutOrganizationInvoiceSettings {
  alias: string;
  invoiceSettings: {
    value: OrganizationInvoiceSettingsV2;
    programType: ProgramType;
    inherit?: boolean;
  };
}

interface GetOrganizationClawbackSettings {
  alias: string;
  programType?: ProgramType;
  clawbackType:
    | OrganizationSettingsV2Types.milestoneClawbackSettings
    | OrganizationSettingsV2Types.materialClawbackSettings;
}

interface PutOrganizationClawbackSettings {
  alias: string;
  clawbackSettings: {
    value: OrganizationMilestoneClawbackSettingsV2 | OrganizationMaterialClawbackSettingsV2;
    programType: ProgramType;
    inherit?: boolean;
  };
  clawbackType:
    | OrganizationSettingsV2Types.milestoneClawbackSettings
    | OrganizationSettingsV2Types.materialClawbackSettings;
}

interface GetOrganizationUnderwritingSettings {
  alias: string;
  programType: ProgramType;
}

interface GetEffectiveOrganizationUnderwritingSettings {
  alias: string;
  programType: ProgramType;
}

export type OrganizationUnderwritingSettingsDoc = OrganizationSettingsV2<OrganizationUnderwritingSettingsV2> & {
  _id: string;
};

export interface OrganizationUnderwritingSettingsStateOverride {
  settings: OrganizationUnderwritingSettingsDoc;
  sourceAlias: string;
}

interface OrganizationUnderwritingSettingsResponse {
  settings: OrganizationUnderwritingSettingsDoc | null;
  stateOverrides: OrganizationUnderwritingSettingsStateOverride[];
}

export interface OrganizationUnderwritingOverrideSummary {
  alias: string;
  name: string;
  hasGeneral: boolean;
  states: string[];
}

interface OrganizationsWithUnderwritingOverridesResponse {
  orgs: OrganizationUnderwritingOverrideSummary[];
}

interface EffectiveOrganizationUnderwritingSettingsResponse {
  settings: OrganizationSettingsV2<OrganizationUnderwritingSettingsV2>;
  sourceAlias: string;
}

interface PutOrganizationUnderwritingSettings {
  alias: string;
  programType: ProgramType;
  value: OrganizationUnderwritingSettingsV2;
  effectiveDate: string;
}

interface GetOrganizationDirectPaySettings {
  alias: string;
  programType?: ProgramType;
}

interface GetOrganizationPaymentPlan {
  alias: string;
  programType?: ProgramType;
}
interface GetOrganizationPaymentSetting {
  alias: string;
}

export interface PostOrganizationReviewSlaSetting {
  alias: string;
  inherit?: boolean;
  programType?: ProgramType;
  enabled?: boolean;
  reviewPriorityTier?: number;
  slaBusinessHoursByMilestone?: OrgV2ReviewSlaSettings['slaBusinessHoursByMilestone'];
}

interface SplitPayProgramNamesResponse {
  programs: {
    programId: string;
    programName: string;
  }[];
}

interface GetOrganizationHoldbackPayoutEventSettings {
  alias: string;
  programType: ProgramType;
}

interface PutOrganizationHoldbackPayoutEventSettings {
  alias: string;
  programType: ProgramType;
  value: HoldbackPayoutEventsSettings;
}

interface GetOrganizationHoldbackPaymentBatchedSettings {
  alias: string;
  programType: ProgramType;
}

interface PutOrganizationHoldbackPaymentBatchedSettings {
  alias: string;
  programType: ProgramType;
  value: HoldbackPaymentBatchedSettings;
}

export interface GetOrganizationPausedSettings {
  alias: string;
  programType: ProgramType;
  settingsType: OrganizationPausedSettingsType;
}

export interface PutOrganizationPausedSettings {
  alias: string;
  programType: ProgramType;
  settingsType: OrganizationPausedSettingsType;
  value: PausedSettings;
}

export interface OrgV2PostPaymentPlanSetting {
  alias: string;
  paymentPlanId?: string;
  inherit: boolean;
  programType: ProgramType;
  effectiveDate?: string;
  planToApplyToExistingAccounts?: string;
}
export interface PutOrganizationPaymentSetting {
  alias: string;
  paymentSettings: DeepPartial<{
    paymentPlanId: string;
    netSuiteVendorId: string;
    inheritBankAccount: boolean;
    inheritNetSuiteVendorId: boolean;
    inheritPaymentPlan: boolean;
    payoutHold: Pick<PayoutHold, 'active' | 'reason'>;
  }>;
}

interface SearchUsersResponse {
  meta: {
    total: number;
    limit: number;
    currentPage: number;
    prevPage?: number;
    nextPage?: number;
  };
  data: Auth0PalmettoFinanceUser[];
}
interface SearchUsersParams {
  orgAlias: string;
  searchUsers: string;
  page?: number;
  limit?: number;
}

interface GetOrganizationStatusResponse {
  status: OrganizationStatus;
}

type OrganizationActivityResponse = any;

interface PaymentSettings {
  paymentPlanId?: string;
  netSuiteVendorId?: string;
  inheritNetSuiteVendorId?: boolean;
  inheritBankAccount?: boolean;
  inheritPaymentPlan?: boolean;
  paymentPlanHistory?: {
    paymentPlanId?: string;
    inheritPaymentPlan?: boolean;
    setBy: Actor;
    dateSet: Date;
  }[];
  payoutHold?: PayoutHold;
  email?: string;
}

type PaymentSettingsWithMetadata = PaymentSettings & {
  scheduleOwnerAlias?: string;
  inherited?: boolean;
};

export type GetOrganizationDirectPaySettingsResponse = {
  data: (OrganizationDirectPaySettingsV2 & { inherited?: boolean })[];
};

export type OrganizationMaintenanceSettingsResponse = {
  id?: string;
  value?: OrganizationMaintenanceSettingsV2Value;
  inherit: boolean;
  effectiveDate?: string;
};

export type PutOrganizationMaintenanceSettingsInput = {
  alias: string;
  programType: ProgramType;
  inherit: boolean;
  effectiveDate?: string;
  value?: {
    cadence: {
      times: number;
      per: { unit: MaintenancePeriodUnit; amount: number };
    };
    maintenanceWindow: { unit: MaintenancePeriodUnit; amount: number };
    maintenanceRate: { unit: MaintenanceRateUnit; amount: number };
    serviceRate: { unit: MaintenanceRateUnit; amount: number };
  };
};

export const flattenOrganizationTree = (organization: OrganizationNode, path = ''): FlatOrganization[] => {
  path += organization.alias + '/';
  const children = organization?.children?.length
    ? organization?.children?.flatMap((o) => flattenOrganizationTree(o, path))
    : [];
  return [{ ...organization, path }, ...children];
};

export const flattenOrganizationSearchTree = (organizations: OrganizationNode[]): FlatOrganization[] => {
  // path += organization.alias + '/';
  const flatOrganizations = organizations.flatMap((organization) => {
    const basePath = organization?.parentAlias ? `${organization.parentAlias}/` : '';
    return flattenOrganizationTree(organization, basePath);
  });
  // const children = organization?.children?.length
  //   ? organization?.children?.flatMap((o) => flattenOrganizationTree(o, path))
  //   : [];
  // return [{ ...organization, path }, ...children];
  return flatOrganizations;
};

export const formatParams = (queryParams: Record<string, any>) => {
  const formattedParams = Object.fromEntries(
    Object.entries(queryParams || {}).map(([key, value]) => [key, String(value)]),
  );
  return new URLSearchParams({ ...formattedParams });
};

const pausedSettingsUrlPaths: Record<OrganizationPausedSettingsType, string> = {
  [OrganizationSettingsTypes.standardHoldbackPausedSettings]: 'standard-holdback-paused-settings',
  [OrganizationSettingsTypes.domesticContentHoldbackPausedSettings]: 'domestic-content-holdback-paused-settings',
  [OrganizationSettingsTypes.safeHarborPausedSettings]: 'safe-harbor-paused-settings',
};

const getPausedSettingsUrlPath = (settingsType: OrganizationPausedSettingsType) =>
  pausedSettingsUrlPaths[settingsType];

export function buildCoverageAreasUrl(alias: string, state?: string, programType?: ProgramType): string {
  const params = new URLSearchParams();
  if (state) params.set('state', state);
  if (programType) params.set('programType', programType);
  const queryString = params.toString();
  return `organizations/${alias}/coverage-areas${queryString ? `?${queryString}` : ''}`;
}

export const organizationApi = api.injectEndpoints({
  endpoints: (build) => ({
    getOrganizationTree: build.query<
      { tree: OrganizationNode[]; flat: FlatOrganization[] },
      GetOrganizationQueryParams | void
    >({
      query: (queryParams) => {
        const formattedParams = Object.fromEntries(
          Object.entries(queryParams || {}).map(([key, value]) => [key, String(value)]),
        );
        return {
          url: `organizations${queryParams ? '?' + new URLSearchParams({ ...formattedParams }).toString() : ''}`,
        };
      },
      transformResponse(baseQueryReturnValue: OrganizationNode[]) {
        if (!baseQueryReturnValue.length) {
          return { tree: [], flat: [] };
        }
        const flat = flattenOrganizationTree(baseQueryReturnValue[0]);
        return { tree: baseQueryReturnValue, flat };
      },
      providesTags: () => {
        return [{ type: 'Organizations', id: 'LIST' }];
      },
    }),
    getOrganizationDetails: build.query<OrganizationNode, GetOrganizationDetailsQueryParams>({
      query: ({ alias, usersPage = 1, usersLimit = 10, includeUsers = false, inviteToken }) => ({
        url: `organizations/${alias}?includeUsers=${includeUsers}&usersPage=${usersPage}&usersLimit=${usersLimit}${inviteToken ? `&inviteToken=${inviteToken}` : ''}`,
      }),
      providesTags: (result) => [{ type: 'Organizations', id: result?.alias }],
    }),
    getOrganizationsWithHardwareExceptions: build.query<
      OrgHardwareExceptionsResponse,
      ListOrgHardwareExceptionsParams
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.search) searchParams.set('search', params.search);
        searchParams.set('limit', String(params.limit));
        searchParams.set('offset', String(params.offset));
        return { url: `organizations/hardware-exceptions?${searchParams.toString()}` };
      },
      providesTags: (result) =>
        (result?.data ?? []).map(({ alias }) => ({ type: 'Organizations' as const, id: alias })),
    }),
    //#region Org Webhooks
    getOrganizationWebhooks: build.query<WebhookClient[], { alias: string }>({
      query: ({ alias }) => ({
        url: `organizations/${alias}/webhooks/registrations`,
      }),
      providesTags: (_result) => [{ type: 'OrganizationWebhooks', id: 'LIST' }],
    }),
    createOrganizationWebhook: build.mutation<WebhookClient, { alias: string; body: DeepPartial<WebhookClient> }>({
      query: ({ alias, body }) => ({
        url: `organizations/${alias}/webhooks/registrations`,
        method: 'POST',
        body,
      }),
      invalidatesTags: () => [{ type: 'OrganizationWebhooks', id: 'LIST' }],
    }),
    getOrganizationWebhooksSubscriptions: build.query<WebhookSubscription[], { registrationId: string }>({
      query: ({ registrationId }) => ({
        url: `webhooks/registrations/${registrationId}/subscriptions`,
      }),
      providesTags: (_result, _error, args) => [
        { type: 'OrganizationWebhooksSubscriptions', id: args.registrationId },
      ],
    }),
    updateOrganizationWebhook: build.mutation<
      WebhookClient,
      { registrationId: string; body: DeepPartial<WebhookClient> }
    >({
      query: ({ registrationId, body }) => ({
        url: `webhooks/registrations/${registrationId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: () => [{ type: 'OrganizationWebhooks', id: 'LIST' }],
    }),
    deleteOrganizationWebhook: build.mutation<{ success: boolean }, { registrationId: string }>({
      query: ({ registrationId }) => ({
        url: `webhooks/registrations/${registrationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'OrganizationWebhooks', id: 'LIST' }],
    }),
    postCreateOrganizationWebhooksSubscription: build.mutation<
      WebhookSubscriptionPostResponse,
      {
        registrationId: string;
        body: Omit<WebhookSubscription, 'id' | 'clientId'>;
      }
    >({
      query: ({ body, registrationId }) => ({
        url: `webhooks/registrations/${registrationId}/subscriptions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'OrganizationWebhooksSubscriptions', id: args.registrationId },
      ],
    }),
    testOrganizationWebhooksSubscription: build.mutation<
      ClientWebhookResponse,
      { registrationId: string; body: TestWebhookSubscription }
    >({
      query: ({ registrationId, body }) => ({
        url: `webhooks/registrations/${registrationId}/subscriptions/test`,
        method: 'POST',
        body,
      }),
    }),
    updateOrganizationWebhooksSubscription: build.mutation<
      WebhookSubscription,
      {
        registrationId: string;
        subscriptionId: string;
        body: DeepPartial<WebhookSubscription>;
      }
    >({
      query: ({ registrationId, subscriptionId, body }) => ({
        url: `webhooks/registrations/${registrationId}/subscriptions/${subscriptionId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'OrganizationWebhooksSubscriptions', id: args.registrationId },
      ],
    }),
    deleteOrganizationWebhooksSubscription: build.mutation<
      { success: boolean },
      { registrationId: string; subscriptionId: string }
    >({
      query: ({ registrationId, subscriptionId }) => ({
        url: `webhooks/registrations/${registrationId}/subscriptions/${subscriptionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'OrganizationWebhooksSubscriptions', id: args.registrationId },
      ],
    }),
    getOrganizationWebhookLogs: build.query<
      WithObjectIdsAsStrings<OutboundWebhookLog>[],
      {
        registrationId: string;
        subscriptionId: string;
        event?: string;
        limit?: number;
        since?: string;
      }
    >({
      query: ({ registrationId, subscriptionId, event, limit, since }) => {
        const params = new URLSearchParams();
        if (event) {
          params.set('event', event);
        }
        params.set('limit', String(limit ?? 0));
        params.set('since', since ?? '');
        return {
          url: `webhooks/registrations/${registrationId}/subscriptions/${subscriptionId}/logs?${params.toString()}`,
        };
      },
      providesTags: (logs) => [{ type: 'OrganizationWebhookLogs', id: logs?.[0]?.subscriptionId }],
    }),
    getOrganizationWebhookStatistics: build.query<
      GetOrganizationWebhookStatisticsResponse[],
      { alias: string; since?: string }
    >({
      query: ({ alias, since }) => ({
        url: `organizations/${alias}/webhooks/registrations/statistics?since=${encodeURIComponent(since ?? '')}`,
      }),
      providesTags: () => [{ type: 'OrganizationWebhookStatistics' }],
    }),
    //#endregion
    getOrganizationPaymentPlanHistory: build.query<
      PaymentPlanHistory[],
      { alias: string; programType?: ProgramType }
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/payment-plan/history/${programType ? programType : ProgramType.solar}`,
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        {
          type: 'OrganizationPaymentPlanHistory',
          id: `${alias}${programType ? `-${programType}` : ''}`,
        },
      ],
    }),
    getOrganizationsPaymentPlanPendingChanges: build.query<
      PendingOrganizationPaymentPlanChanges[],
      { programType?: ProgramType; organizationAlias?: string }
    >({
      query: (params) => ({
        url: `organizations/payment-plan/pending-changes?${formatParams({
          programType: params.programType || ProgramType.solar,
          ...(params.organizationAlias ? { alias: params.organizationAlias } : {}),
        })}`,
      }),
      providesTags: (_result, _error, { organizationAlias, programType }) => [
        organizationAlias
          ? {
              type: 'OrganizationPendingPaymentPlanChanges',
              id: `${organizationAlias}${programType ? `-${programType}` : ''}`,
            }
          : { type: 'OrganizationPendingPaymentPlanChanges' },
      ],
    }),
    cancelOrganizationPaymentPlanPendingChanges: build.mutation<void, { settingsId?: string }>({
      query: ({ settingsId }) => ({
        url: `organizations/payment-plan/pending-changes/${settingsId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result) => [{ type: 'OrganizationPendingPaymentPlanChanges' }],
    }),
    sendOrganizationPaymentPlanPendingChangesEmail: build.mutation<void, { settingsId?: string }>({
      query: ({ settingsId }) => ({
        url: `organizations/payment-plan/pending-changes/${settingsId}/send-email`,
        method: 'POST',
      }),
    }),
    acceptOrganizationPaymentPlanPendingChanges: build.mutation<void, { settingsId?: string }>({
      query: ({ settingsId }) => ({
        url: `organizations/payment-plan/pending-changes/${settingsId}/accept`,
        method: 'POST',
      }),
      invalidatesTags: (_result) => [{ type: 'OrganizationPendingPaymentPlanChanges' }],
    }),
    getPerformProgress: build.query<boolean, string>({
      query: (accountId) => ({
        url: `organizations/perform-progress/${accountId}`,
      }),
      transformResponse: (response: any) => {
        return Boolean(response);
      },
      providesTags: () => [{ type: 'OrganizationProgress', id: 'perform-progress' }],
    }),
    getOrganizationStatus: build.query<GetOrganizationStatusResponse, string>({
      query: (alias) => ({
        url: `organizations/${alias}/status`,
      }),
      transformResponse: (response: any) => {
        return response;
      },
      providesTags: (_result, _error, alias) => [{ type: 'OrganizationStatus', id: alias }],
    }),
    createOrganization: build.mutation<OrganizationNode, CreateOrganizationParams>({
      query: (organization) => ({
        url: `organizations`,
        method: 'POST',
        body: organization,
      }),
      invalidatesTags: [{ type: 'Organizations', id: 'LIST' }],
    }),
    editOrganization: build.mutation<
      OrganizationNode,
      { organization: Partial<CreateOrganizationParams>; alias: string }
    >({
      query: ({ organization, alias }) => ({
        url: `organizations/${alias}`,
        method: 'PATCH',
        body: organization,
      }),
      invalidatesTags: (result) => [
        { type: 'Organizations', id: 'LIST' },
        { type: 'Organizations', id: result?.alias },
      ],
    }),
    getOrganizationActivity: build.query<OrganizationActivityResponse, { alias: string }>({
      query: ({ alias }) => ({
        url: `organizations/${alias}/activity`,
      }),
      providesTags: (result) =>
        result
          ? [
              ...(result.data ?? []).map(({ id }: { id: string }) => ({
                type: 'OrganizationActivityEvent' as const,
                id,
              })),
              { type: 'OrganizationActivity', id: 'LIST' },
            ]
          : [{ type: 'OrganizationActivity', id: 'LIST' }],
    }),
    getOrganizationPaymentPlan: build.query<
      WithObjectIdsAsStrings<PaymentPlan & PaymentSettingsWithMetadata>,
      GetOrganizationPaymentPlan
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/payment-plan?programType=${programType ? programType : ProgramType.solar}`,
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        {
          type: 'OrganizationPaymentPlan',
          id: `${alias}${programType ? `-${programType}` : ''}`,
        },
      ],
    }),
    updateOrganizationPaymentPlan: build.mutation<
      OrganizationSettingsV2 & OrgV2PaymentPlanSettings,
      OrgV2PostPaymentPlanSetting
    >({
      query: ({ alias, paymentPlanId, inherit, programType, effectiveDate, planToApplyToExistingAccounts }) => ({
        url: `organizations/${alias}/payment-plan`,
        method: 'POST',
        body: {
          paymentPlanId,
          inherit,
          programType,
          effectiveDate,
          planToApplyToExistingAccounts,
        },
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationPaymentPlan', id: `${alias}-${programType}` },
        { type: 'OrganizationPaymentSettings', id: alias },
        {
          type: 'OrganizationPaymentPlanHistory',
          id: `${alias}-${programType}`,
        },
        { type: 'OrganizationPendingPaymentPlanChanges', id: alias },
      ],
    }),
    getOrganizationReviewSlaSettings: build.query<
      OrgV2ReviewSlaSettingsResponse,
      { alias: string; programType?: ProgramType }
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/qc-priority?programType=${programType ?? ProgramType.solar}`,
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationReviewSla', id: `${alias}-${programType ?? ProgramType.solar}` },
      ],
    }),
    updateOrganizationReviewSlaSettings: build.mutation<
      OrganizationSettingsV2 & OrgV2ReviewSlaSettings,
      PostOrganizationReviewSlaSetting
    >({
      query: ({ alias, inherit, programType, enabled, reviewPriorityTier, slaBusinessHoursByMilestone }) => ({
        url: `organizations/${alias}/qc-priority`,
        method: 'POST',
        body: {
          inherit,
          programType,
          enabled,
          reviewPriorityTier,
          slaBusinessHoursByMilestone,
        },
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationReviewSla', id: `${alias}-${programType ?? ProgramType.solar}` },
        { type: 'OrganizationActivity', id: 'LIST' },
      ],
    }),
    getOrganizationMaintenanceSettings: build.query<
      OrganizationMaintenanceSettingsResponse,
      { alias: string; programType: ProgramType }
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/maintenance-settings?programType=${programType}`,
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationMaintenanceSettings', id: `${alias}-${programType}` },
      ],
    }),
    putOrganizationMaintenanceSettings: build.mutation<
      OrganizationMaintenanceSettingsResponse,
      PutOrganizationMaintenanceSettingsInput
    >({
      query: ({ alias, programType, inherit, effectiveDate, value }) => ({
        url: `organizations/${alias}/maintenance-settings`,
        method: 'PUT',
        body: { programType, inherit, effectiveDate, ...(value ? { value } : {}) },
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationMaintenanceSettings', id: `${alias}-${programType}` },
        { type: 'OrganizationActivity', id: 'LIST' },
      ],
    }),
    putOrganizationPaymentSetting: build.mutation<PaymentSettingsWithMetadata, PutOrganizationPaymentSetting>({
      query: ({ alias, paymentSettings }) => ({
        url: `organizations/${alias}/payment-setting`,
        method: 'PUT',
        body: paymentSettings,
      }),
      invalidatesTags: (_result, _error, { alias }) => [
        { type: 'OrganizationPaymentSettings', id: alias },
        { type: 'OrganizationPaymentPlan', id: alias },
        { type: 'OrganizationPaymentPlanHistory', id: alias },
      ],
    }),
    getOrganizationPaymentSettings: build.query<PaymentSettingsWithMetadata, GetOrganizationPaymentSetting>({
      query: ({ alias }) => ({
        url: `organizations/${alias}/payment-settings`,
      }),
      providesTags: (_result, _error, { alias }) => [{ type: 'OrganizationPaymentSettings', id: alias }],
    }),
    getOrganizationImpactedPaymentPlanAccounts: build.query<
      {
        parentOrganization: string;
        organizationsWithAccounts: string[];
        totalAccountsImpacted: number;
        accounts: OrgAccountsImpacted[];
      },
      {
        alias: string;
        currentPaymentPlanId: string;
        proposedPaymentPlanId: string;
      }
    >({
      query: ({ alias, currentPaymentPlanId, proposedPaymentPlanId }) => ({
        url: `organizations/${alias}/payment-plan/impacted-accounts?currentPaymentPlanId=${currentPaymentPlanId}&proposedPaymentPlanId=${proposedPaymentPlanId}&includeSubOrgs=true&accountLimit=10`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'OrganizationPaymentAccounts', id: arg.alias }],
    }),
    getOrganizationPaymentAccounts: build.query<FinancialAccountDocument[], GetOrganizationPaymentSchedule>({
      query: ({ alias }) => ({
        url: `organizations/${alias}/payment-accounts`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'OrganizationPaymentAccounts', id: arg.alias }],
    }),
    getOrganizationEmailSettings: build.query<{ emailSettings: EmailSetting[] }, GetOrganizationEmailSettings>({
      query: ({ alias }) => ({
        url: `organizations/${alias}/email-settings`,
      }),
      providesTags: (_result, _error, { alias }) => [{ type: 'OrganizationEmailSettings', id: alias }],
    }),
    putOrganizationEmailSettings: build.mutation<
      { emailSettings: EmailSetting[] },
      { alias: string; emailSettings: EmailSetting[]; signatoryNames: SignatoryName[] }
    >({
      query: ({ alias, emailSettings, signatoryNames }) => ({
        url: `organizations/${alias}/email-settings`,
        method: 'PUT',
        body: { emailSettings, signatoryNames },
      }),
      invalidatesTags: (_result, _error, { alias }) => [{ type: 'OrganizationEmailSettings', id: alias }],
    }),
    getSplitPayProgramNames: build.query<SplitPayProgramNamesResponse, void>({
      query: () => ({
        url: `organizations/split-pay-settings/program-names`,
      }),
    }),
    getOrganizationDirectPaySettings: build.query<
      GetOrganizationDirectPaySettingsResponse,
      GetOrganizationDirectPaySettings
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/direct-pay-settings${programType ? `?programType=${programType}` : ''}`,
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        {
          type: 'OrganizationDirectPaySetting',
          id: `${alias}${programType ? `-${programType}` : ''}`,
        },
      ],
    }),
    getDirectPayMaterialVendors: build.query<
      {
        materialVendors: Array<{
          directPayMaterialVendorAlias: string;
          directPayMaterialVendorName: string;
          subCompanies: Array<{ name: string; id: string }>;
        }>;
      },
      void
    >({
      query: () => ({
        url: `organizations/direct-pay-settings/material-vendors`,
      }),
    }),
    putOrganizationDirectPaySettings: build.mutation<
      OrganizationDirectPaySettingsV2,
      { alias: string; directPaySettings: OrganizationDirectPaySettingsV2 }
    >({
      query: ({ alias, directPaySettings }) => ({
        url: `organizations/${alias}/direct-pay-settings`,
        method: 'PUT',
        body: { ...directPaySettings },
      }),
      invalidatesTags: (_result, _error, { alias, directPaySettings }) => [
        { type: 'OrganizationDirectPaySetting', id: alias },
        {
          type: 'OrganizationDirectPaySetting',
          id: `${alias}${directPaySettings.programType ? `-${directPaySettings.programType}` : ''}`,
        },
      ],
    }),
    updateBankAccount: build.mutation<
      FinancialAccountRegister,
      { paymentAccount: FinancialAccountRegister; alias: string }
    >({
      query: ({ paymentAccount, alias }) => ({
        url: `organizations/${alias}/payment-account`,
        method: 'PUT',
        body: paymentAccount,
      }),
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [{ type: 'OrganizationPaymentSettings', id: arg?.alias }],
    }),
    getOrgPriceSheetMappings: build.query<
      PriceSheetMapping<any>[],
      { alias: string; id?: string; programType?: ProgramType }
    >({
      query: ({ alias, id, programType }) => ({
        url: `organizations/${alias}/pricing/mappings${id ? `/${id}` : ''}${programType ? `?programType=${programType}` : ''}`,
      }),
      transformResponse(response: PriceSheetMapping<any>[]) {
        return response?.toSorted((a: any, b: any) => {
          const stateA = a.state.toLowerCase();
          const stateB = b.state.toLowerCase();

          if (stateA < stateB) {
            return -1;
          }
          if (stateA > stateB) {
            return 1;
          }
          return 0;
        });
      },
      providesTags: (_result, _error, { alias, id, programType }) => [
        {
          type: 'OrganizationPriceSheetMappings',
          id: `${alias}-${programType}`,
          mappingId: id,
        },
      ],
    }),
    searchUsers: build.query<SearchUsersResponse, SearchUsersParams>({
      query: ({ orgAlias, searchUsers, page = 1, limit = 10 }) => {
        return {
          url: `organizations/${orgAlias}/users?search=${searchUsers}&page=${page}&limit=${limit}`,
        };
      },
      providesTags: (_result, _error, { orgAlias }) => [{ type: 'Users', id: orgAlias }],
    }),
    getOrganizationListFilters: build.query<any, Record<any, any>>({
      query: () => {
        return {
          url: `organizations/filters`,
        };
      },
      transformResponse: (response) => {
        return response;
      },
    }),
    searchOrganizations: build.query<
      {
        total: number;
        pageNum: number;
        organizations: OrganizationNode[];
        flat: FlatOrganization[];
      },
      {
        searchTerm?: string;
        advancedFilters?: string;
        useRootOrganization?: boolean;
      }
    >({
      query: (queryParams) => {
        return {
          url: `organizations/search${queryParams ? '?' + formatParams(queryParams).toString() : ''}`,
        };
      },
      keepUnusedDataFor: 300,
      transformResponse(response: any) {
        const { data, pageNum, total } = response || {};
        const { organizations } = data || {};
        const flat = organizations?.length ? flattenOrganizationSearchTree(organizations) : [];
        return { flat, pageNum, organizations, total };
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.flat?.map(({ id }: { id: string }) => ({
                type: 'Organizations' as const,
                id,
              })) ?? {}),
              { type: 'Organizations', id: 'LIST' },
            ]
          : [{ type: 'Organizations', id: 'LIST' }],
    }),
    searchOrganizationsByUser: build.query<
      { organizations: OrganizationNode[] },
      { search: string; page?: number; limit?: number }
    >({
      query: ({ search, page = 1, limit = 10 }) => {
        return {
          url: `organizations/search/by-user?search=${search}&page=${page}&limit=${limit}`,
        };
      },
      transformResponse: (response: { organizations: OrganizationNode[] }) => {
        return { organizations: response.organizations };
      },
      providesTags: (_result, _error, { search }) => [{ type: 'OrganizationsByUser', id: search }],
    }),
    getOrganizationClawbackSettings: build.query<
      OrganizationSettingsV2<OrganizationClawbackSettingsV2>,
      GetOrganizationClawbackSettings
    >({
      query: ({ alias, programType, clawbackType: typeClawbackSettings }) => ({
        url: `organizations/${alias}/clawback-settings/${typeClawbackSettings}?programType=${programType}`,
      }),
      providesTags: (_result, _error, { alias, programType, clawbackType }) => [
        {
          type: 'OrganizationClawbackSettings',
          id: `${alias}-${clawbackType}${programType ? `-${programType}` : ''}`,
        },
      ],
    }),
    putOrganizationClawbackSettings: build.mutation<
      OrganizationSettingsV2<OrganizationClawbackSettingsV2>,
      PutOrganizationClawbackSettings
    >({
      query: ({ alias, clawbackSettings, clawbackType }) => ({
        url: `organizations/${alias}/clawback-settings/${clawbackType}`,
        method: 'PUT',
        body: { ...clawbackSettings },
      }),
      invalidatesTags: (_result, _error, { alias, clawbackSettings, clawbackType }) => [
        {
          type: 'OrganizationClawbackSettings',
          id: `${alias}-${clawbackType}${clawbackSettings.programType ? `-${clawbackSettings.programType}` : ''}`,
        },
      ],
    }),
    getOrganizationUnderwritingSettings: build.query<
      OrganizationUnderwritingSettingsResponse,
      GetOrganizationUnderwritingSettings
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/underwriting-settings?programType=${programType}`,
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        {
          type: 'OrganizationUnderwritingSettings',
          id: `${alias}-${programType}`,
        },
      ],
    }),
    getEffectiveOrganizationUnderwritingSettings: build.query<
      EffectiveOrganizationUnderwritingSettingsResponse | null,
      GetEffectiveOrganizationUnderwritingSettings
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/underwriting-settings/effective?programType=${programType}`,
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationUnderwritingSettings', id: `${alias}-${programType}-effective` },
      ],
    }),
    getOrganizationsWithUnderwritingOverrides: build.query<
      OrganizationsWithUnderwritingOverridesResponse,
      GetOrganizationUnderwritingSettings
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/underwriting-settings/org-overrides?programType=${programType}`,
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationUnderwritingSettings', id: `${alias}-${programType}-org-overrides` },
      ],
    }),
    putOrganizationUnderwritingSettings: build.mutation<
      OrganizationSettingsV2<OrganizationUnderwritingSettingsV2>,
      PutOrganizationUnderwritingSettings
    >({
      query: ({ alias, programType, value, effectiveDate }) => ({
        url: `organizations/${alias}/underwriting-settings?programType=${programType}`,
        method: 'PUT',
        body: { value, effectiveDate },
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationUnderwritingSettings', id: `${alias}-${programType}` },
        { type: 'OrganizationUnderwritingSettings', id: `${alias}-${programType}-effective` },
      ],
    }),
    deleteOrganizationUnderwritingSetting: build.mutation<
      void,
      { alias: string; programType: ProgramType; settingsId: string }
    >({
      query: ({ alias, settingsId }) => ({
        url: `organizations/${alias}/underwriting-settings/${settingsId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        { type: 'OrganizationUnderwritingSettings', id: `${alias}-${programType}` },
        { type: 'OrganizationUnderwritingSettings', id: `${alias}-${programType}-effective` },
      ],
    }),
    getOrganizationInvoiceSettings: build.query<
      OrganizationSettingsV2<OrganizationInvoiceSettingsV2>,
      GetOrganizationInvoiceSettings
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/invoice-settings?programType=${programType}`,
      }),
    }),
    putOrganizationInvoiceSettings: build.mutation<
      OrganizationSettingsV2<OrganizationInvoiceSettingsV2>,
      PutOrganizationInvoiceSettings
    >({
      query: ({ alias, invoiceSettings }) => ({
        url: `organizations/${alias}/invoice-settings`,
        method: 'PUT',
        body: { ...invoiceSettings },
      }),
    }),
    getOrganizationHoldbackPayoutEventSettings: build.query<
      OrganizationHoldbackPayoutEventsSettingsDocument,
      GetOrganizationHoldbackPayoutEventSettings
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/holdback-payout-events-settings?programType=${programType}`,
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        {
          type: 'OrganizationHoldbackPayoutEventSettings',
          id: `${alias}${programType ? `-${programType}` : ''}`,
        },
      ],
    }),
    putOrganizationHoldbackPayoutEventSettings: build.mutation<
      OrganizationHoldbackPayoutEventsSettingsDocument,
      PutOrganizationHoldbackPayoutEventSettings
    >({
      query: ({ alias, programType, value }) => ({
        url: `organizations/${alias}/holdback-payout-events-settings?programType=${programType}`,
        method: 'PUT',
        body: { ...value },
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        {
          type: 'OrganizationHoldbackPayoutEventSettings',
          id: `${alias}${programType ? `-${programType}` : ''}`,
        },
      ],
    }),
    getOrganizationHoldbackPaymentBatchedSettings: build.query<
      OrganizationHoldbackPaymentBatchedSettingsDocument,
      GetOrganizationHoldbackPaymentBatchedSettings
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/holdback-payment-batched-settings?programType=${programType}`,
      }),
      providesTags: (_result, _error, { alias, programType }) => [
        {
          type: 'OrganizationHoldbackPaymentBatchedSettings',
          id: `${alias}${programType ? `-${programType}` : ''}`,
        },
      ],
    }),
    putOrganizationHoldbackPaymentBatchedSettings: build.mutation<
      OrganizationHoldbackPaymentBatchedSettingsDocument,
      PutOrganizationHoldbackPaymentBatchedSettings
    >({
      query: ({ alias, programType, value }) => ({
        url: `organizations/${alias}/holdback-payment-batched-settings?programType=${programType}`,
        method: 'PUT',
        body: { ...value },
      }),
      invalidatesTags: (_result, _error, { alias, programType }) => [
        {
          type: 'OrganizationHoldbackPaymentBatchedSettings',
          id: `${alias}${programType ? `-${programType}` : ''}`,
        },
      ],
    }),
    getOrganizationPausedSettings: build.query<OrganizationPausedSettingsDocument, GetOrganizationPausedSettings>({
      query: ({ alias, programType, settingsType }) => ({
        url: `organizations/${alias}/${getPausedSettingsUrlPath(settingsType)}?programType=${programType}`,
      }),
      providesTags: (_result, _error, { alias, programType, settingsType }) => [
        {
          type: 'OrganizationPausedSettings',
          id: `${alias}-${settingsType}${programType ? `-${programType}` : ''}`,
        },
      ],
    }),
    putOrganizationPausedSettings: build.mutation<OrganizationPausedSettingsDocument, PutOrganizationPausedSettings>(
      {
        query: ({ alias, programType, settingsType, value }) => ({
          url: `organizations/${alias}/${getPausedSettingsUrlPath(settingsType)}?programType=${programType}`,
          method: 'PUT',
          body: { ...value },
        }),
        invalidatesTags: (_result, _error, { alias, programType, settingsType }) => [
          {
            type: 'OrganizationPausedSettings',
            id: `${alias}-${settingsType}${programType ? `-${programType}` : ''}`,
          },
        ],
      },
    ),
    getOrgEquipmentSettings: build.query<
      Partial<OrganizationSettingsV2<OrganizationEquipmentSettingsV2>> & {
        inherit: boolean;
      },
      { alias: string }
    >({
      query: ({ alias }) => ({
        url: `organizations/${alias}/equipment-settings`,
      }),
      providesTags: (_result, _error, { alias }) => [{ type: 'OrganizationEquipmentSettings', id: alias }],
    }),
    setOrgEquipmentSettings: build.mutation<
      OrganizationSettingsV2<OrganizationEquipmentSettingsV2>,
      {
        alias: string;
        equipmentSettings: {
          value: OrganizationEquipmentSettingsV2;
          inherit: boolean;
        };
      }
    >({
      query: ({ alias, equipmentSettings }) => ({
        url: `organizations/${alias}/equipment-settings`,
        method: 'PUT',
        body: { ...equipmentSettings },
      }),
      invalidatesTags: (_result, _error, { alias }) => [{ type: 'OrganizationEquipmentSettings', id: alias }],
    }),
    getOrganizationCoverageAreaSettings: build.query<
      OrganizationCoverageAreaSettingsDocument[],
      { alias: string; state?: string; programType?: ProgramType }
    >({
      query: ({ alias, state, programType }) => ({
        url: buildCoverageAreasUrl(alias, state, programType),
      }),
      transformResponse: (response: { data: OrganizationCoverageAreaSettingsDocument[] }) => response.data,
      providesTags: (_result, _error, { alias }) => [{ type: 'OrganizationCoverageAreaSettings', id: alias }],
    }),
    putOrganizationCoverageAreaSettings: build.mutation<
      OrganizationCoverageAreaSettingsDocument,
      { alias: string; state: string; programType: ProgramType; value: Omit<CoverageAreaValue, 'setBy' | 'history'> }
    >({
      query: ({ alias, state, programType, value }) => ({
        url: `organizations/${alias}/coverage-areas`,
        method: 'PUT',
        body: { state, programType, value },
      }),
      invalidatesTags: (_result, _error, { alias }) => [{ type: 'OrganizationCoverageAreaSettings', id: alias }],
    }),
    getOrganizationStateLicenses: build.query<
      (StateLicenseNumber & { organizationAlias: string })[],
      { alias: string; programType?: ProgramType }
    >({
      query: ({ alias, programType }) => ({
        url: `organizations/${alias}/state-licenses${programType ? `?programType=${programType}` : ''}`,
      }),
    }),
  }),
});

export const {
  useGetOrganizationTreeQuery,
  useLazyGetOrganizationTreeQuery,
  useGetOrganizationDetailsQuery,
  useGetOrganizationsWithHardwareExceptionsQuery,
  useGetOrganizationWebhooksQuery,
  useCreateOrganizationWebhookMutation,
  useUpdateOrganizationWebhookMutation,
  useDeleteOrganizationWebhookMutation,
  useGetOrganizationWebhooksSubscriptionsQuery,
  usePostCreateOrganizationWebhooksSubscriptionMutation,
  useUpdateOrganizationWebhooksSubscriptionMutation,
  useDeleteOrganizationWebhooksSubscriptionMutation,
  useTestOrganizationWebhooksSubscriptionMutation,
  useGetOrganizationWebhookLogsQuery,
  useGetOrganizationWebhookStatisticsQuery,
  useCreateOrganizationMutation,
  useGetOrganizationActivityQuery,
  useGetOrganizationPaymentPlanHistoryQuery,
  useEditOrganizationMutation,
  useGetOrganizationPaymentPlanQuery,
  useUpdateOrganizationPaymentPlanMutation,
  useGetOrganizationReviewSlaSettingsQuery,
  useUpdateOrganizationReviewSlaSettingsMutation,
  useGetOrganizationEmailSettingsQuery,
  usePutOrganizationEmailSettingsMutation,
  useGetSplitPayProgramNamesQuery,
  useGetDirectPayMaterialVendorsQuery,
  useGetOrganizationDirectPaySettingsQuery,
  usePutOrganizationDirectPaySettingsMutation,
  useUpdateBankAccountMutation,
  useGetOrgPriceSheetMappingsQuery,
  useLazyGetOrgPriceSheetMappingsQuery,
  useSearchUsersQuery,
  usePutOrganizationPaymentSettingMutation,
  useGetOrganizationPaymentSettingsQuery,
  useGetOrganizationPaymentAccountsQuery,
  useGetOrganizationListFiltersQuery,
  useLazySearchOrganizationsByUserQuery,
  useSearchOrganizationsQuery,
  useLazySearchOrganizationsQuery,
  useGetPerformProgressQuery,
  useGetOrganizationStatusQuery,
  useLazyGetOrganizationDetailsQuery,
  useGetOrganizationsPaymentPlanPendingChangesQuery,
  useAcceptOrganizationPaymentPlanPendingChangesMutation,
  useGetOrganizationClawbackSettingsQuery,
  usePutOrganizationClawbackSettingsMutation,
  useGetOrganizationUnderwritingSettingsQuery,
  useGetEffectiveOrganizationUnderwritingSettingsQuery,
  useLazyGetOrganizationsWithUnderwritingOverridesQuery,
  usePutOrganizationUnderwritingSettingsMutation,
  useDeleteOrganizationUnderwritingSettingMutation,
  useGetOrganizationImpactedPaymentPlanAccountsQuery,
  useCancelOrganizationPaymentPlanPendingChangesMutation,
  useSendOrganizationPaymentPlanPendingChangesEmailMutation,
  useGetOrganizationInvoiceSettingsQuery,
  usePutOrganizationInvoiceSettingsMutation,
  useGetOrganizationHoldbackPayoutEventSettingsQuery,
  usePutOrganizationHoldbackPayoutEventSettingsMutation,
  useGetOrganizationHoldbackPaymentBatchedSettingsQuery,
  usePutOrganizationHoldbackPaymentBatchedSettingsMutation,
  useGetOrganizationPausedSettingsQuery,
  usePutOrganizationPausedSettingsMutation,
  useGetOrgEquipmentSettingsQuery,
  useSetOrgEquipmentSettingsMutation,
  useGetOrganizationStateLicensesQuery,
  useGetOrganizationCoverageAreaSettingsQuery,
  usePutOrganizationCoverageAreaSettingsMutation,
  useGetOrganizationMaintenanceSettingsQuery,
  usePutOrganizationMaintenanceSettingsMutation,
} = organizationApi;
